import React, { FC } from 'react';
import { Col, Row } from 'antd';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { BrobizzTable } from '../../../common-components/BrobizzTable';
import TransactionsStore from '../../../stores/TransactionsStore';
import ProductStore from '../../../stores/ProductStore';
import ContentStore from '../../../stores/ContentStore';
import useStore from '../../../hooks/useStore';
import usePagination from '../../../hooks/usePagination';
import { ManchetContentCamptonBook22Grey } from '../../../layout/globalFonts';
// eslint-disable-next-line import/no-cycle
import PrivateProductCardModal from '../../product-features/product-card/PrivateProductCardModal';
// eslint-disable-next-line import/no-named-as-default, import/no-named-as-default-member -- default hook module; eslint parser false-positive on this file
import useTransactionsAndUsageColumns from '../../../hooks/useTransactionsAndUsageColumns';
import LoadingSpinner from '../../../common-components/LoadingSpinner';
import { Transaction } from '../../../swagger/api';
import { getInvoiceStatusDisplayInfo } from '../../../utils/invoiceStatusUtils';
import { ColorScheme } from '../../../layout/colorScheme';
import {
  getPrivateTableRowBorderTopColor,
  getPrivateTransactionRowFillKind
} from '../../../utils/tableRowDivider';

const StyledBrobizzTable = styled(BrobizzTable)`
  table colgroup {
    display: none !important;
  }

  table col {
    display: none !important;
    width: auto !important;
  }

  table {
    table-layout: auto !important;
    --border-grey: none !important;
    .ant-table-tbody > tr.ant-table-row:hover > td {
      cursor: default !important;
    }

    .ant-table-tbody:has(> tr.ant-table-measure-row)
      > tr.ant-table-row:nth-child(even):not(.outstanding-row):not(.credit-row):hover
      > td {
      background: #f7f7f7 !important;
      cursor: default !important;
    }

    .ant-table-tbody:has(> tr.ant-table-measure-row)
      > tr.ant-table-row:nth-child(odd):not(.outstanding-row):not(.credit-row):hover
      > td {
      background: white !important;
      cursor: default !important;
    }

    .ant-table-tbody:not(:has(> tr.ant-table-measure-row))
      > tr.ant-table-row:nth-child(odd):not(.outstanding-row):not(.credit-row):hover
      > td {
      background: #f7f7f7 !important;
      cursor: default !important;
    }

    .ant-table-tbody:not(:has(> tr.ant-table-measure-row))
      > tr.ant-table-row:nth-child(even):not(.outstanding-row):not(.credit-row):hover
      > td {
      background: white !important;
      cursor: default !important;
    }

    .ant-table-tbody > tr.outstanding-row.ant-table-row:hover > td {
      background: ${ColorScheme.OutstandingBackground} !important;
      cursor: default !important;
    }

    .ant-table-tbody > tr.credit-row.ant-table-row:hover > td {
      background: ${ColorScheme.CreditBackground} !important;
      cursor: default !important;
    }
  }

  thead.ant-table-thead,
  tbody.ant-table-tbody {
    display: contents !important;
  }

  thead.ant-table-thead > tr,
  tbody.ant-table-tbody > tr:not(.ant-table-placeholder),
  .ant-table-thead > tr,
  .ant-table-tbody > tr:not(.ant-table-placeholder) {
    display: grid !important;
    grid-template-columns: 0.9fr 1.5fr 1.7fr 1.3fr 1fr 1.1fr !important;
    @media (min-width: 1380px) {
      grid-template-columns: 1.5fr 1.5fr 2.1fr 1.4fr 1fr 1.2fr !important;
    }
    align-items: center !important;
    width: 100% !important;
  }

  .ant-table-thead > tr {
    min-height: 70px !important;
  }

  .ant-table-tbody > tr > td {
    display: flex !important;
    align-items: center !important;
    overflow: hidden;
    min-width: 0;
    height: 100% !important;
  }

  .ant-table-thead > tr > th {
    display: flex !important;
    align-items: center !important;
    overflow: hidden;
    min-width: 0;
    min-height: 70px !important;
    box-sizing: border-box;
    justify-content: flex-start !important;
    border-top: none !important;
  }

  .ant-table-thead > tr > th:nth-child(4),
  .ant-table-thead > tr > th.status-column-header {
    padding-left: 35px !important;
    justify-content: flex-start !important;
  }

  @media (max-width: 1400px) {
    .ant-table-thead > tr > th:nth-child(4),
    .ant-table-thead > tr > th.status-column-header {
      padding-left: 10px !important;
    }
  }

  .ant-table-thead > tr > th:nth-child(5) {
    justify-content: flex-end !important;
    padding-right: 16px;
  }

  // Body cell alignment - status column (4th)
  .ant-table-tbody > tr > td:nth-child(4) {
    justify-content: flex-start !important;
  }

  /* Status cells with icon badge: no left padding */
  .ant-table-tbody > tr > td.status-column-with-badge {
    padding-left: 0 !important;
  }

  /* Status cells without icon: padding-left 35px, 10px under 1400px */
  .ant-table-tbody > tr > td.status-column-plain {
    padding-left: 35px !important;
  }

  @media (max-width: 1400px) {
    .ant-table-tbody > tr > td.status-column-plain {
      padding-left: 10px !important;
    }
  }

  .ant-table-tbody > tr > td:nth-child(5) {
    justify-content: flex-end !important;
  }

  .ant-table-thead > tr > th:first-child,
  .ant-table-tbody > tr:not(.ant-table-placeholder) > td:first-child {
    padding-left: 40px !important;
  }

  .ant-table-thead > tr > th:last-child,
  .ant-table-tbody > tr:not(.ant-table-placeholder) > td:last-child {
    padding-right: 40px !important;
  }

  .ant-table-tbody:has(> tr.ant-table-measure-row)
    > tr.ant-table-row:nth-child(even):not(.outstanding-row):not(.credit-row)
    > td {
    background-color: #f7f7f7 !important;
    border: none !important;
  }

  .ant-table-tbody:has(> tr.ant-table-measure-row)
    > tr.ant-table-row:nth-child(odd):not(.outstanding-row):not(.credit-row)
    > td {
    background-color: white !important;
    border: none !important;
  }

  .ant-table-tbody:not(:has(> tr.ant-table-measure-row))
    > tr.ant-table-row:nth-child(odd):not(.outstanding-row):not(.credit-row)
    > td {
    background-color: #f7f7f7 !important;
    border: none !important;
  }

  .ant-table-tbody:not(:has(> tr.ant-table-measure-row))
    > tr.ant-table-row:nth-child(even):not(.outstanding-row):not(.credit-row)
    > td {
    background-color: white !important;
    border: none !important;
  }

  /* No top border on measure row or first data row — avoids double line vs thead / card */
  .ant-table-tbody > tr:first-child,
  .ant-table-tbody > tr:first-child > td,
  .ant-table-tbody > tr.ant-table-measure-row + tr.ant-table-row,
  .ant-table-tbody > tr.ant-table-measure-row + tr.ant-table-row > td {
    border-top: none !important;
  }

  .ant-table-tbody > tr {
    cursor: default !important;
  }

  .ant-table-tbody > tr.outstanding-row > td {
    background-color: ${ColorScheme.OutstandingBackground} !important;
  }

  .ant-table-tbody > tr.credit-row > td {
    background-color: ${ColorScheme.CreditBackground} !important;
  }

  .ant-table-cell {
    p {
      margin: 0;
    }
  }

  // Empty text styling - use single column grid
  .ant-table-tbody > tr.ant-table-placeholder {
    display: grid !important;
    grid-template-columns: 1fr !important;

    > td {
      grid-column: 1;
      width: 100%;
    }
  }
`;

interface TransactionsAndUsageTableDesktopProps {
  transactionsStore: TransactionsStore;
  contentStore: ContentStore;
  showOutstandingOnly: boolean;
  isTableOnProductCard: boolean;
  transactions: Transaction[];
  isLoading: boolean;
  isPrivateCustomer?: boolean;
}

const TransactionsAndUsageTableDesktop: FC<TransactionsAndUsageTableDesktopProps> = (props) => {
  const {
    transactionsStore,
    contentStore,
    showOutstandingOnly,
    isTableOnProductCard,
    transactions,
    isLoading,
    isPrivateCustomer = false
  } = props;
  const productStore = useStore(ProductStore) as ProductStore;
  const { productItemsStore, productCardStore } = productStore;

  const columns = useTransactionsAndUsageColumns(
    contentStore,
    isTableOnProductCard,
    showOutstandingOnly,
    isPrivateCustomer
  );

  const pagination = showOutstandingOnly
    ? usePagination(
        transactionsStore.outstandingPagenumber,
        transactionsStore.numberOfOutstandingTransactions,
        transactionsStore.pageSizeOutstanding,
        transactionsStore.setOutstandingCurrentPage
      )
    : usePagination(
        transactionsStore.transactionsPageNumber,
        transactionsStore.filteredNumberOfTransactions,
        transactionsStore.pageSize,
        transactionsStore.setTransactionsCurrentPage
      );

  const privateRowProps = (record: Transaction, index?: number) => {
    const i = index ?? 0;
    const prev = i > 0 ? transactions[i - 1] : null;
    const currKind = getPrivateTransactionRowFillKind(record, i);
    const prevKind = prev ? getPrivateTransactionRowFillKind(prev, i - 1) : null;
    const color = getPrivateTableRowBorderTopColor(prevKind, currKind);
    return {
      style: { borderTop: `1px solid ${color}` } as React.CSSProperties
    };
  };

  return (
    <>
      <Row>
        <Col span={24}>
          {isPrivateCustomer ? (
            <StyledBrobizzTable
              dataSource={transactions}
              rowKey="id"
              columns={columns}
              showSorterTooltip={false}
              pagination={!isTableOnProductCard && pagination}
              sticky
              loading={{
                spinning: isLoading,
                indicator: <LoadingSpinner />
              }}
              scroll={{ x: true }}
              onRow={privateRowProps}
              rowClassName={(record: Transaction) => {
                const statusInfo = getInvoiceStatusDisplayInfo(
                  {
                    invoiceStatus: record.invoiceStatus as string,
                    invoiceType: record.invoiceType,
                    transactionType: record.transactionType,
                    revenueOperationType: record.revenueOperationType,
                    dueDate: record.dueDate
                  },
                  'private'
                );
                return statusInfo.rowClassName;
              }}
              locale={{
                emptyText: (
                  <Row justify="center" align="middle" style={{ height: '100%' }}>
                    <ManchetContentCamptonBook22Grey>
                      {
                        contentStore.cmsStore.getContent('HereYouWillSeeConsumptionWhenRegistered')
                          ?.content
                      }
                    </ManchetContentCamptonBook22Grey>
                  </Row>
                )
              }}
            />
          ) : (
            <BrobizzTable
              dataSource={transactions}
              rowKey="id"
              columns={columns}
              showSorterTooltip={false}
              pagination={!isTableOnProductCard && pagination}
              sticky
              loading={{
                spinning: isLoading,
                indicator: <LoadingSpinner />
              }}
              scroll={{ x: true }}
              locale={{
                emptyText: (
                  <Row justify="center" align="middle" style={{ height: '100%' }}>
                    <ManchetContentCamptonBook22Grey>
                      {showOutstandingOnly
                        ? contentStore.cmsStore.getContent('NoOutstandings')?.content
                        : contentStore.cmsStore.getContent(
                            'HereYouWillSeeConsumptionWhenRegistered'
                          )?.content}
                    </ManchetContentCamptonBook22Grey>
                  </Row>
                )
              }}
            />
          )}
        </Col>
      </Row>
      {productCardStore.isModalVisible && productItemsStore.product && !isTableOnProductCard && (
        <PrivateProductCardModal
          contentStore={contentStore}
          productCardStore={productCardStore}
          product={productItemsStore.product}
        />
      )}
    </>
  );
};
TransactionsAndUsageTableDesktop.defaultProps = {
  isPrivateCustomer: false
};

export default observer(TransactionsAndUsageTableDesktop);
